import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Users, Euro, TrendingUp, Copy, CheckCircle, ExternalLink,
  Zap, LogOut, Loader2, Mail, Lock, Eye, EyeOff, RefreshCw,
  Calendar, Building2, Gift, Award, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnerDashboardPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (token) {
      fetchDashboard(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchDashboard = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/partners/dashboard/${token}`);
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
        setIsLoggedIn(true);
      } else {
        localStorage.removeItem('partner_token');
        setIsLoggedIn(false);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      localStorage.removeItem('partner_token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoginLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/partners/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('partner_token', data.token);
        toast.success('Connexion réussie !');
        fetchDashboard(data.token);
      } else {
        setError(data.detail || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partner_token');
    setIsLoggedIn(false);
    setDashboardData(null);
    toast.success('Déconnexion réussie');
  };

  const copyReferralLink = () => {
    if (dashboardData?.referral_link) {
      navigator.clipboard.writeText(dashboardData.referral_link);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const refreshData = () => {
    const token = localStorage.getItem('partner_token');
    if (token) {
      setLoading(true);
      fetchDashboard(token);
    }
  };

  const getTierBadge = (tier) => {
    const tiers = {
      bronze: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '🥉' },
      silver: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: '🥈' },
      gold: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: '🥇' },
      platinum: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '💎' }
    };
    return tiers[tier] || tiers.bronze;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-slate-50 to-white p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border-slate-200">
            <CardHeader className="text-center space-y-4">
              <Link to="/" className="flex justify-center">
                <div className="p-3 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl">
                  <Zap className="h-8 w-8 text-white" />
                </div>
              </Link>
              <div>
                <CardTitle className="text-2xl font-bold">Espace Partenaire</CardTitle>
                <p className="text-slate-600 mt-2">Connectez-vous pour accéder à votre dashboard</p>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={credentials.email}
                      onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="pl-10"
                      required
                      data-testid="partner-login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      required
                      data-testid="partner-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
                  disabled={loginLoading}
                  data-testid="partner-login-submit"
                >
                  {loginLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-600">
                <p>Pas encore partenaire ?</p>
                <Link to="/partenaires" className="text-blue-600 hover:underline font-medium">
                  Rejoindre le programme
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Dashboard
  const { partner, stats, referrals, referral_link } = dashboardData || {};
  const tierInfo = getTierBadge(partner?.tier);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">SmartFacture AI</span>
            </Link>
            <Badge className={tierInfo.color}>
              {tierInfo.icon} Partenaire {partner?.tier?.charAt(0).toUpperCase() + partner?.tier?.slice(1)}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={refreshData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">
            Bonjour, {partner?.contact_name} 👋
          </h1>
          <p className="text-slate-600 mt-1">{partner?.cabinet_name}</p>
        </div>

        {/* Referral Link Card */}
        <Card className="mb-8 bg-gradient-to-r from-blue-600 to-cyan-500 text-white border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Votre lien de parrainage
                </h3>
                <p className="text-blue-100 text-sm">
                  Partagez ce lien avec vos clients pour les inscrire automatiquement
                </p>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="flex-1 md:flex-none bg-white/20 backdrop-blur rounded-lg px-4 py-2 font-mono text-sm truncate max-w-md">
                  {referral_link}
                </div>
                <Button
                  onClick={copyReferralLink}
                  className="bg-white text-blue-600 hover:bg-blue-50 shrink-0"
                  data-testid="copy-referral-link"
                >
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-blue-100">
              <span>Code partenaire :</span>
              <Badge className="bg-white/20 text-white border-white/30 font-mono">
                {partner?.referral_code}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Clients référés</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_referrals || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-emerald-100">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Clients actifs</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.active_clients || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-amber-100">
                  <Euro className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Commission/mois</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.monthly_commission?.toFixed(2) || '0.00'}€</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-purple-100">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total gagné</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_earned?.toFixed(2) || '0.00'}€</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referrals Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-slate-600" />
              Clients référés
            </CardTitle>
          </CardHeader>
          <CardContent>
            {referrals && referrals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Entreprise</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date d'inscription</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((client, index) => (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-900">{client.email}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{client.company_name || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={
                            client.subscription_status === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-100 text-slate-600'
                          }>
                            {client.subscription_status === 'active' ? 'Actif' : 'Essai'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun client référé</h3>
                <p className="text-slate-600 mb-4">
                  Partagez votre lien de parrainage pour commencer à gagner des commissions
                </p>
                <Button onClick={copyReferralLink} className="bg-blue-600 hover:bg-blue-700">
                  <Copy className="h-4 w-4 mr-2" />
                  Copier mon lien
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commission Info */}
        <Card className="mt-8 border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-blue-100">
                <Award className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  Votre taux de commission : {partner?.commission_rate}
                </h3>
                <p className="text-slate-600 text-sm">
                  Vous gagnez {partner?.commission_rate} sur chaque abonnement payé par vos clients référés.
                  Plus vous référez de clients, plus votre niveau partenaire augmente et votre commission aussi !
                </p>
                <div className="flex gap-4 mt-3 text-sm">
                  <span className="text-slate-500">🥉 Bronze: 15%</span>
                  <span className="text-slate-500">🥈 Silver: 20%</span>
                  <span className="text-slate-500">🥇 Gold: 25%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-600">
          <p>© 2026 SmartFacture AI - Programme Partenaire</p>
          <p className="mt-1">
            Questions ? <a href="mailto:partenaires@smartfacture-ai.com" className="text-blue-600 hover:underline">partenaires@smartfacture-ai.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PartnerDashboardPage;
